import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRouteSession } from '@web/app'
import { BrandMark } from '@layerit/ui/components/brand-mark'
import { ProfileMenu } from '@web/components/profile-menu'
import { ThemeMenu } from '@layerit/ui/components/theme-menu'
import { cn } from '@layerit/ui/lib/utils'
import { publicSiteHref } from '@web/site'
import { APP_NAME } from '@layerit/brand'

export interface OnboardingStep {
	label: string
	path: string
}

export const ONBOARDING_STEPS = [
	{ label: 'Your name', path: '/onboarding/name' },
	{ label: 'Your organisation', path: '/onboarding/organisation' },
] as const

interface OnboardingShellProps {
	step: number
	title: string
	description: ReactNode
	children: ReactNode
	footer?: ReactNode
}

export function OnboardingShell({ step, title, description, children, footer }: OnboardingShellProps) {
	const { state, signOut } = useRouteSession()
	const navigate = useNavigate()
	const wordmarkHref = publicSiteHref('/')

	const wordmark = (
		<>
			<BrandMark size={28} />
			<span className="text-sm font-semibold tracking-tight text-ink">{APP_NAME}</span>
		</>
	)

	return (
		<div className="flex min-h-screen flex-col bg-surface">
			<div className="flex shrink-0 items-center justify-between gap-4 px-6 py-6">
				{wordmarkHref ? (
					<a className="flex w-fit items-center gap-2.5 rounded-md transition-opacity hover:opacity-80" href={wordmarkHref}>
						{wordmark}
					</a>
				) : (
					<div className="flex w-fit items-center gap-2.5">{wordmark}</div>
				)}

				<div className="flex items-center gap-1">
					<ThemeMenu />
					{(state.status === 'ready' || state.status === 'organisation') && (
						<ProfileMenu user={state.user} signOut={signOut} navigate={navigate} />
					)}
				</div>
			</div>

			<div className="mx-auto flex w-full max-w-104 flex-1 flex-col justify-center px-6 pb-12">
				<nav aria-label="Setup progress">
					<ol className="grid grid-cols-2 gap-2">
						{ONBOARDING_STEPS.map((entry, index) => {
							const done = index < step
							const current = index === step
							return (
								<li key={entry.path} className="flex flex-col gap-2">
									<span aria-hidden className={cn('h-0.5 rounded-full', index <= step ? 'bg-accent' : 'bg-line-2')} />
									<span className={cn('flex items-center gap-1.5 text-xs', current ? 'text-ink' : 'text-ink-3')}>
										<span
											aria-hidden
											className={cn(
												'size-1.5 rounded-full',
												current ? 'bg-accent' : done ? 'bg-accent/40' : 'bg-line',
											)}
										/>
										{entry.label}
									</span>
								</li>
							)
						})}
					</ol>
					<p className="sr-only">
						Step {step + 1} of {ONBOARDING_STEPS.length}: {ONBOARDING_STEPS.at(step)?.label ?? ''}
					</p>
				</nav>

				<div className="mt-8 flex flex-col">
					<h1 className="font-display text-2xl text-ink">{title}</h1>
					<p className="mt-2.5 text-sm leading-relaxed text-ink-3">{description}</p>

					<div className="mt-7">{children}</div>

					{footer && <div className="mt-6">{footer}</div>}
				</div>
			</div>
		</div>
	)
}
